import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight, Mic } from "lucide-react";
import { useApp } from "@/context/AppContext";

export const SimulationOverlay = () => {
  const { simActive, simStep, setSimStep, simSteps, stopSimulation } = useApp();
  const navigate = useNavigate();
  const step = simSteps[simStep];

  useEffect(() => {
    if (simActive && step?.module) {
      navigate(step.module);
    }
  }, [simStep, simActive, step?.module, navigate]);

  if (!simActive || !step) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.18 }}
        className="fixed bottom-4 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] rounded-xl border border-[--hairline] bg-[--shell-2] shadow-2xl shadow-black/60"
        data-testid="simulation-overlay"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[--divider]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[--six-crimson] lane-live" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-[--tx-2]">Executive Simulation</span>
            <span className="text-[11px] font-mono text-[--tx-3]">Step {step.step} / {simSteps[simSteps.length - 1]?.step ?? simSteps.length}</span>
          </div>
          <button onClick={stopSimulation} data-testid="simulation-close-button" className="text-[--tx-3] hover:text-[--tx-1] p-1 rounded transition-colors">
            <X size={15} />
          </button>
        </div>
        <div className="px-4 py-3 max-h-[46vh] overflow-y-auto thin-scroll">
          <h4 className="font-display text-sm font-semibold text-[--tx-1]" data-testid="simulation-step-title">{step.title}</h4>
          <p className="text-[13px] text-[--tx-2] mt-1.5 leading-relaxed">{step.narrative}</p>
          <div className="mt-3 rounded-lg bg-[--panel-dark] border border-[--divider] p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[--amber] mb-1.5">
              <Mic size={11} /> Presenter talk track
            </div>
            <p className="text-xs text-[--tx-2] leading-relaxed italic">{step.talk_track}</p>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[--divider]">
          <button
            onClick={() => setSimStep(Math.max(0, simStep - 1))}
            disabled={simStep === 0}
            data-testid="simulation-prev-button"
            className="inline-flex items-center gap-1 text-xs font-medium text-[--tx-2] hover:text-[--tx-1] disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <button
            onClick={() => navigate(step.module)}
            data-testid="simulation-goto-module-button"
            className="inline-flex items-center gap-1 text-xs font-medium text-[--ai] hover:text-[#35B7B1] transition-colors"
          >
            Open module <ArrowUpRight size={13} />
          </button>
          {simStep < simSteps.length - 1 ? (
            <button
              onClick={() => setSimStep(simStep + 1)}
              data-testid="simulation-next-button"
              className="inline-flex items-center gap-1 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3 py-1.5 text-xs font-semibold text-white transition-colors"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={stopSimulation}
              data-testid="simulation-finish-button"
              className="inline-flex items-center gap-1 rounded-lg bg-[--green] hover:brightness-110 active:scale-[0.98] px-3 py-1.5 text-xs font-semibold text-white transition-colors"
            >
              End simulation
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
